(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const loadPoster = video => {
    if (!video.dataset.poster || video.poster) return;
    const url = new URL(video.dataset.poster, location.href);
    const maximum = Number(url.searchParams.get('width')) || 700;
    const width = Math.min(maximum, Math.max(160, Math.ceil(video.getBoundingClientRect().width * (window.devicePixelRatio || 1) / 80) * 80));
    url.searchParams.set('width', String(width));
    video.poster = url.href;
  };
  const posterObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      loadPoster(entry.target);
      posterObserver.unobserve(entry.target);
    });
  }, {rootMargin:'800px 240px'});
  const videos = new Set();
  const visible = new Set();
  const positions = new Map();
  let scrolling = false;
  let touching = false;
  let settleTimer;
  const keyFor = video => video.querySelector('source')?.dataset.src || video.currentSrc;
  const pause = video => {
    if (!video.paused) positions.set(keyFor(video), video.currentTime);
    video.pause();
  };
  const syncPlayback = () => {
    const playing = new Set();
    videos.forEach(video => {
      if (!visible.has(video) || scrolling || touching || document.hidden || reduced.matches) pause(video);
    });
    videos.forEach(video => {
      const key = keyFor(video);
      if (!visible.has(video) || scrolling || touching || document.hidden || reduced.matches || playing.has(key)) return;
      playing.add(key);
      loadPoster(video);
      if (!video.dataset.loaded) {
        video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
        video.dataset.loaded = 'true';
        video.load();
      }
      if (!video.paused) return;
      const saved = positions.get(key);
      if (video.readyState && saved !== undefined && Math.abs(video.currentTime - saved) > 0.1) video.currentTime = saved;
      video.play().catch(() => {});
    });
  };
  const pauseForScroll = (delay = 180) => {
    scrolling = true;
    videos.forEach(pause);
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => { scrolling = false; syncPlayback(); }, delay);
  };
  const videoObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.intersectionRatio > 0) visible.add(entry.target);
      else visible.delete(entry.target);
    });
    syncPlayback();
  }, { threshold: [0, 0.01, 0.5, 1] });
  const registerVideos = scope => {
    scope.querySelectorAll('[data-demo-track] video, splide-component[data-image-slider="true"] video').forEach(video => {
      if (videos.has(video)) return;
      videos.add(video);
      if (video.dataset.poster) posterObserver.observe(video);
      video.controls = false;
      video.removeAttribute('controls');
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.disablePictureInPicture = true;
      video.addEventListener('loadedmetadata', () => {
        const saved = positions.get(keyFor(video));
        if (saved !== undefined) video.currentTime = saved;
      });
      video.addEventListener('canplay', syncPlayback);
      videoObserver.observe(video);
    });
  };
  document.addEventListener('scroll', () => pauseForScroll(), { passive: true, capture: true });
  document.addEventListener('wheel', () => pauseForScroll(), { passive: true });
  document.addEventListener('touchstart', () => { touching = true; videos.forEach(pause); }, { passive: true });
  ['touchend', 'touchcancel'].forEach(name => document.addEventListener(name, () => { touching = false; pauseForScroll(); }, { passive: true }));
  document.addEventListener('visibilitychange', syncPlayback);
  window.addEventListener('pagehide', () => videos.forEach(pause));
  window.addEventListener('pageshow', syncPlayback);
  reduced.addEventListener('change', syncPlayback);
  const init = (scope = document) => {
    scope.querySelectorAll('details:not([data-source-disclosure-ready])').forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;
      details.dataset.sourceDisclosureReady = 'true';
      summary.setAttribute('role', 'button');
      const updateExpanded = () => summary.setAttribute('aria-expanded', String(details.open));
      details.addEventListener('toggle', updateExpanded);
      updateExpanded();
    });
    scope.querySelectorAll('.animate-section:not([data-reveal-ready])').forEach((section) => {
      section.dataset.revealReady = 'true';
      section.classList.remove('animate--hidden');
      section.classList.add('animate--shown');
    });
    scope.querySelectorAll('comparison-slider:not([data-ready])').forEach((slider) => {
      slider.dataset.ready = 'true';
      const input = slider.querySelector('input[type="range"]');
      const overlay = slider.querySelector('.comparison-slider__overlay');
      const image = overlay?.querySelector('img');
      const line = slider.querySelector('.comparison-slider__line');
      if (!input || !overlay || !line) return;
      input.setAttribute('aria-label', 'Compare before and after');
      const update = () => {
        overlay.style.width = `${input.value}%`;
        line.style.left = `${input.value}%`;
        if (image) { image.style.width = `${slider.clientWidth}px`; image.style.maxWidth = 'none'; }
        input.setAttribute('aria-valuetext', `${input.value}% before`);
      };
      input.addEventListener('input', update);
      new ResizeObserver(update).observe(slider);
      update();
    });
    scope.querySelectorAll('splide-component[data-image-slider="true"]:not([data-source-ready])').forEach((carousel) => {
      carousel.dataset.sourceReady = 'true';
      const shell = carousel.querySelector('.splide');
      const track = carousel.querySelector('.splide__track');
      const list = carousel.querySelector('.splide__list');
      const original = [...list.children];
      if (!original.length) return;
      shell.classList.add('is-initialized', 'is-active', 'is-overflow', 'splide--ltr');
      track.style.touchAction = 'pan-y';
      shell.setAttribute('role', 'region');
      shell.setAttribute('aria-label', 'Customer videos');
      for (const slide of original.slice(-3).reverse()) { const clone = slide.cloneNode(true); clone.dataset.clone = 'true'; clone.setAttribute('aria-hidden', 'true'); list.prepend(clone); }
      for (const slide of original.slice(0, 3)) { const clone = slide.cloneNode(true); clone.dataset.clone = 'true'; clone.setAttribute('aria-hidden', 'true'); list.append(clone); }
      let index = 3;
      let moving = false;
      const gap = () => Number(window.innerWidth < 750 ? carousel.dataset.gapMobile : carousel.dataset.gapDesktop);
      const step = () => original[0].getBoundingClientRect().width + gap();
      const render = (animate = false) => {
        list.style.transition = animate && !reduced.matches ? 'transform 400ms ease' : 'none';
        list.style.transform = `translateX(${-index * step()}px)`;
        pauseForScroll(animate ? 460 : 180);
      };
      const move = (direction) => { if (moving) return; moving = true; index += direction; render(true); window.setTimeout(() => { if (index >= original.length + 3) index = 3; if (index < 3) index = original.length + 2; render(false); moving = false; }, reduced.matches ? 0 : 410); };
      const arrows = carousel.querySelector('.splide__arrows');
      ['prev', 'next'].forEach((direction) => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = `splide__arrow splide__arrow--${direction} color-accent-1`;
        button.setAttribute('aria-label', direction === 'prev' ? 'Previous customer videos' : 'Next customer videos');
        button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" aria-hidden="true"><path d="m15.5 0-4.3 4.3L26.9 20 11.2 35.7l4.3 4.3 20-20z"/></svg>';
        button.addEventListener('click', () => move(direction === 'prev' ? -1 : 1)); arrows?.append(button);
      });
      let start = null;
      track.addEventListener('pointerdown', (event) => { start = event.clientX; });
      track.addEventListener('pointerup', (event) => { if (start !== null && Math.abs(event.clientX - start) > 30) move(event.clientX < start ? 1 : -1); start = null; });
      track.addEventListener('pointercancel', () => { start = null; });
      new ResizeObserver(() => render(false)).observe(track);
      render();
    });
    registerVideos(scope);
  };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
